import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects } from '../api/queries/projects';
import {
  useServiceRates,
  useCreateTimeEntry,
  effectiveRates,
} from '../api/queries/time-entries';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { ProjectOptions } from '../components/ProjectOptions';
import { TimeRangeFields, type TimeRangeValue } from '../components/TimeRangeFields';
import { MoneyInput } from '../components/MoneyInput';
import { SoldAssetPicker } from '../components/SoldAssetPicker';
import { MatterSelect } from '../components/MatterSelect';
import { SaleDateField, startOfLocalDay } from '../components/SaleDateField';
import { isFlatRate } from '../utils/time-entry';
import './NewTimeEntryPage.css';

export function NewTimeEntryPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const create = useCreateTimeEntry();

  const initialProjectId = (() => {
    const v = searchParams.get('project_id');
    if (!v) return 0;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : 0;
  })();

  const [projectId, setProjectId] = useState<number>(initialProjectId);
  // ?matter_id= from a matter's row on the project page (0061).
  const [matterId, setMatterId] = useState<number | null>(() => {
    const n = Number(searchParams.get('matter_id'));
    return initialProjectId > 0 && Number.isInteger(n) && n > 0 ? n : null;
  });
  const [serviceRateId, setServiceRateId] = useState<number>(0);
  // End and duration start EMPTY — only the start is worth guessing, and now
  // is nearly always right. A seeded six-minute end and a "0.10" duration were
  // placeholders that had to be cleared before they could be replaced, and
  // they left the triad with nothing blank to fill, which is the state its
  // arithmetic is quietest in. Matches Quick add on /time-entries.
  const [range, setRange] = useState<TimeRangeValue>(
    () => ({ start: Date.now(), end: null, hours: '' }),
  );
  const [description, setDescription] = useState('');

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const projectsQ = useProjects();
  // Fetch the project's EFFECTIVE rate sheet when one is selected, so its
  // project/client overrides surface ahead of the global defaults.
  const selectedProject = projectsQ.data?.projects.find((p) => p.id === projectId);
  const ratesQ = useServiceRates(selectedProject?.id);

  // When projects load and there's an initial pick, auto-select a default
  // rate so the form is fillable in one shot.
  useEffect(() => {
    if (serviceRateId === 0 && ratesQ.data?.service_rates.length) {
      const eff = effectiveRates(ratesQ.data.service_rates);
      if (eff.length) setServiceRateId(eff[0]!.id);
    }
  }, [ratesQ.data, serviceRateId]);

  const rates = effectiveRates(ratesQ.data?.service_rates ?? []);
  const selectedRate = rates.find((r) => r.id === serviceRateId);
  const flat = isFlatRate(selectedRate);
  // A flat-fee rate's amount is only the default — each entry bills its own
  // fee (0057). Re-seeded when the rate changes so a previous service's fee
  // never carries over.
  const [fee, setFee] = useState('');
  // The drive this charge sells, if any (0058) — flat-fee rates only.
  const [assetId, setAssetId] = useState<number | null>(null);
  // A product-sale rate (0059) makes the entry a SALE: a sale date, no time or
  // hours, and the device it sold.
  const sale = selectedRate?.is_product_sale === true;
  const rateAmount = selectedRate?.amount;
  useEffect(() => {
    setFee(flat && rateAmount !== undefined ? rateAmount : '');
  }, [serviceRateId, flat, rateAmount]);

  if (projectsQ.isPending) return <p className="nte-status">Loading projects…</p>;
  if (projectsQ.error) {
    return <p className="nte-status nte-error">Could not load projects.</p>;
  }
  const projects = projectsQ.data?.projects ?? [];
  if (projects.length === 0) {
    return (
      <div className="nte-noprojects">
        <h2>No projects yet</h2>
        <p>Time is logged against a project. Create one first.</p>
        <Link to="/projects/new" className="btn-cyan-primary">Create a project</Link>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (projectId <= 0) {
      setFieldErrors({ project_id: 'Select a project' });
      return;
    }
    if (serviceRateId <= 0) {
      setFieldErrors({ service_rate_id: 'Select a service rate' });
      return;
    }
    if (range.start === null) {
      setFieldErrors({ date: 'Pick a date' });
      return;
    }
    if (!sale && range.end !== null && range.end <= range.start) {
      setFieldErrors({ ended_at_epoch: 'End time must be after the start time' });
      return;
    }
    // The duration field opens blank, so an unfilled one would post '' and come
    // back as a 400 from the API's `^\d+(\.\d{1,2})?$`. A flat fee bills
    // once, so its duration may stay blank — but one that IS entered is checked.
    const hoursBlank = sale || range.hours.trim() === '';
    if (!(flat && hoursBlank)
        && (!/^\d+(\.\d{1,2})?$/.test(range.hours.trim()) || Number(range.hours) <= 0)) {
      setFieldErrors({ hours: 'Enter a duration, or an end time' });
      return;
    }
    if (flat && !/^\d+(\.\d{1,2})?$/.test(fee.trim())) {
      setFieldErrors({ flat_amount: 'Enter the fee for this entry' });
      return;
    }

    try {
      await create.mutateAsync({
        project_id: projectId,
        service_rate_id: serviceRateId,
        hours: hoursBlank ? null : range.hours.trim(),
        ...(flat ? { flat_amount: fee.trim() } : {}),
        ...(sale ? { asset_id: assetId } : {}),
        matter_id: matterId,
        // A sale is dated, not timed — no span, and the date at local midnight.
        date: sale ? startOfLocalDay(range.start) : range.start,
        description: description.trim(),
        // Both-or-neither: only record the span when an end was entered.
        ...(!sale && range.end !== null
          ? { started_at_epoch: range.start, ended_at_epoch: range.end }
          : {}),
      });
      navigate('/time-entries');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to log time on this project.");
        return;
      }
      // The API's own reason — e.g. "That asset is already sold on another time entry".
      setTopError(apiErrorText(err, 'Could not log the time entry.'));
    }
  };

  const money = /^\d+(\.\d{1,2})?$/;
  const estimated = !selectedRate
    ? null
    : flat
      ? (money.test(fee) ? Number(fee) : null)
      : (money.test(range.hours) ? Number(range.hours) * Number(selectedRate.amount) : null);

  return (
    <section className="nte-section">
      <header className="nte-header">
        <h2>Log Time</h2>
        <button
          type="button"
          className="btn-text"
          onClick={() => navigate('/time-entries')}
        >
          ← Back to time entries
        </button>
      </header>

      <form className="nte-form" onSubmit={onSubmit} noValidate>
        <div className="nte-row">
          <label className="nte-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={projectId}
              onChange={(e) => {
                setProjectId(Number(e.target.value));
                // Reset rate so the project's effective sheet re-derives
                setServiceRateId(0);
                setMatterId(null); // a matter belongs to one project
              }}
              required
            >
              <option value={0}>Select a project…</option>
              <ProjectOptions projects={projects} />
            </select>
            {fieldErrors['project_id'] && (
              <span className="field-error">{fieldErrors['project_id']}</span>
            )}
          </label>

          <label className="nte-field">
            <span>Service rate *</span>
            <select
              className="form-input"
              value={serviceRateId}
              onChange={(e) => setServiceRateId(Number(e.target.value))}
              required
              disabled={ratesQ.isPending || projectId === 0}
            >
              <option value={0}>
                {projectId === 0
                  ? 'Pick a project first'
                  : ratesQ.isPending
                  ? 'Loading…'
                  : 'Select a rate…'}
              </option>
              {rates.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.project_override_for
                    ? ' (project rate)'
                    : r.client_override_for
                    ? ' (client rate)'
                    : ''}{' '}
                  —{' '}
                  {Number(r.amount).toLocaleString(undefined, {
                    style: 'currency',
                    currency: 'USD',
                  })}
                  /{r.rate_type === 'hourly' ? 'hr' : 'flat'}
                </option>
              ))}
            </select>
            {fieldErrors['service_rate_id'] && (
              <span className="field-error">{fieldErrors['service_rate_id']}</span>
            )}
          </label>
        </div>

        <MatterSelect
          className="nte-field"
          projectId={projectId || null}
          value={matterId}
          onChange={setMatterId}
          error={fieldErrors['matter_id']}
        />

        {flat && (
          <label className="nte-field">
            <span>Flat fee for this entry *</span>
            <MoneyInput
              value={fee}
              onChange={setFee}
              disabled={create.isPending}
              aria-label="Flat fee for this entry"
            />
            {fieldErrors['flat_amount'] && (
              <span className="field-error">{fieldErrors['flat_amount']}</span>
            )}
          </label>
        )}

        {sale && (
          <SoldAssetPicker
            className="nte-field"
            id="nte-asset"
            value={assetId}
            onChange={setAssetId}
            disabled={create.isPending}
            error={fieldErrors['asset_id']}
          />
        )}

        {sale ? (
          <SaleDateField
            className="nte-field"
            id="nte-sale-date"
            value={range.start}
            onChange={(v) => setRange({ ...range, start: v })}
            disabled={create.isPending}
            error={fieldErrors['date']}
          />
        ) : (
          <TimeRangeFields
            value={range}
            onChange={setRange}
            disabled={create.isPending}
            idPrefix="nte"
            flatFee={flat}
            errors={{
              start: fieldErrors['date'],
              end: fieldErrors['ended_at_epoch'],
              hours: fieldErrors['hours'],
            }}
          />
        )}

        <label className="nte-field nte-field-full">
          <span>Description *</span>
          <textarea
            className="form-input nte-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            required
            maxLength={10000}
            placeholder="Imaging of subject's iPhone — completed extraction, verified hash"
          />
          {fieldErrors['description'] && (
            <span className="field-error">{fieldErrors['description']}</span>
          )}
        </label>

        {estimated !== null && (
          <div className="nte-estimate">
            Estimated value:{' '}
            <strong>
              {estimated.toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
              })}
            </strong>
          </div>
        )}

        {topError && (
          <p className="nte-error-banner" role="alert">
            {topError}
          </p>
        )}

        <div className="nte-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/time-entries')}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={
              create.isPending ||
              projectId <= 0 ||
              serviceRateId <= 0 ||
              range.start === null ||
              (!flat && range.hours.trim().length === 0) ||
              (flat && fee.trim().length === 0) ||
              description.trim().length === 0
            }
          >
            {create.isPending ? 'Saving…' : 'Log time'}
          </button>
        </div>
      </form>
    </section>
  );
}
